import { type HistoryData } from '@fpsm/cxy-types';

import { type Repub, REPUBS } from '@cx-modules/constants';

import {
	type marketMeta,
	type OVER_UNDER,
	type SCORE,
	type SPREAD,
	type WIN,
} from '../constants';

export type Line = {
	absolute: string;
	name: string;
	sign: string;
};

export type OcPlusRepub = Extract<Repub, 'OC' | 'US'>;

export function isOcPlusRepub(repub: Repub): repub is OcPlusRepub {
	return repub === REPUBS.OC.name || repub === REPUBS.US.name;
}

export const REPUB_COUNTRY = {
	OC: 'uk' as const,
	US: 'us' as const,
} as const;

export type RepubCountry = (typeof REPUB_COUNTRY)[OcPlusRepub];

export type MarketDisplayNames = Record<RepubCountry, keyof typeof marketMeta>;

export type Sport = {
	eventId?: number;
	name: string;
};

export type Subevent = {
	categoryGroupId: number;
	categoryGroupName: string;
	categoryId: number;
	categoryName: string;
	eventDisplayName: string;
	eventId: number;
	subeventEndTime: string;
	subeventId: number;
	subeventName: string;
	subeventStartTime: string;
	teamData: SubeventTeamData;
	timestamp: string;
};

export type Market = {
	betTypeId: number;
	marketClassification: MarketClassification;
	marketDisplayName: string;
	marketDisplayNameMap: MarketDisplayNames;
	marketId: number;
	marketName: string;
	marketPropType: string;
};

export type MarketClassification =
	| typeof OVER_UNDER
	| typeof SCORE
	| typeof SPREAD
	| typeof WIN;

export type Bet = {
	betId: number;
	betName: string;
	bookmakerCode: string;
	line?: Line;
	oddsAmerican: string;
	oddsDecimal: number;
	oddsFractional: string;
	parentBetId: number;
};

export type TeamData = {
	badgeUrl?: string;
	name: string;
	nickName?: string;
	shortName?: string;
};

export type SubeventTeamData = {
	away: TeamData;
	home: TeamData;
};
export type StatsTableColumn = {
	average?: {
		last10?: number;
		last20?: number;
		last5?: number;
	};
	averageCalculation?: string;
	primaryStat: boolean;
	stat: string;
	statLevel: 'BET' | 'PARTICIPANT' | 'TEAM';
	type: 'NUMBER' | 'PERCENTAGE' | 'STRING' | 'TIMESTAMP';
	value: string | number;
};

export type StatsTableRow = {
	cells: StatsTableCell[];
};

export type StatsTableCell = {
	betResult?: 'WON' | 'LOST' | 'UNDETERMINED';
	stat?: string;
	value?: number | string;
};

export type StatsTable = {
	columns: StatsTableColumn[];
	rows: StatsTableRow[];
};

export type SportsData = {
	history: HistoryData[];
	historyCount: number;
	hitRates: {
		last10: number;
		last20: number;
		last5: number;
	};
	participant: Participant;
	stat: string;
	statsTable?: StatsTable;
	trends: {
		last10: { hitRate?: number; trend?: string };
		last20: { hitRate?: number; trend?: string };
		last5: { hitRate?: number; trend?: string };
	};
};

export type History = {
	awayTeam: SportsDataTeam;
	homeTeam: SportsDataTeam;
	name: string;
	participant: HistoryParticipant;
	startTime: string;
};

export type SportsDataTeam = TeamData & {
	participantId: string;
	side: ParticipantSide;
};

export type HistoryParticipant = {
	betResult: BetResult;
	betStat: number;
	side: ParticipantSide;
};

export type BetResult = 'LOST' | 'WON' | 'UNDETERMINED';

export type ParticipantSide = 'AWAY' | 'HOME';

export type ParticipantType = 'PLAYER' | 'TEAM';

export type Participant = {
	badgeUrl?: string;
	imageUrl?: string;
	name: string;
	nickName?: string;
	number?: number;
	participantId: string;
	position?: string;
	shortName?: string;
	side: ParticipantSide;
	team?: SportsDataTeam;
	type: ParticipantType;
};

export type Q4Type = 'DIAMOND_IN_THE_ROUGH' | 'HOTTEST';
